/**
 * Save to Space Modal Component
 * Allows users to save a query to an existing category or create a new category
 */

import React from 'react';
import {
  TextField,
  Typography,
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { AppDialog } from './dialog';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { formatRelativeTime } from '../utils/date';
import { getChartPalette } from '../theme/charts';
import { ModeContext } from '../theme/ThemeProvider';

interface SaveToSpaceModalProps {
  open: boolean;
  onClose: () => void;
  spaces: Array<{ 
    space_id: string; 
    name: string; 
    description?: string | null;
    created_at: string;
    query_count: number;
  }>;
  loading: boolean;
  error: string | null;
  selectedSpaceId: string | null;
  setSelectedSpaceId: (v: string | null) => void;
  newSpaceName: string;
  setNewSpaceName: (v: string) => void;
  onSubmit: () => void;
  creating: boolean;
  submitError: string | null;
}

export default function SaveToSpaceModal({
  open,
  onClose,
  spaces,
  loading,
  error,
  selectedSpaceId,
  setSelectedSpaceId,
  newSpaceName,
  setNewSpaceName,
  onSubmit,
  creating,
  submitError,
}: SaveToSpaceModalProps) {
  const { branding } = React.useContext(ModeContext);
  const chartPalette = React.useMemo(() => getChartPalette(branding), [branding]);
  const [searchQuery, setSearchQuery] = React.useState('');
  
  // Filter and sort spaces
  const filteredAndSortedSpaces = React.useMemo(() => {
    let filtered = spaces;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = spaces.filter(space =>
        space.name.toLowerCase().includes(query)
      );
    }
    
    // Sort by created_at descending (most recent first)
    return [...filtered].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [spaces, searchQuery]);
  
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Save Insight to Space"
      busy={creating}
      dividers
      primaryAction={{
        label: 'Save',
        onClick: onSubmit,
        disabled: !selectedSpaceId && !newSpaceName.trim(),
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 1 }}>
          <Typography variant="subtitle2">
            Choose a space
          </Typography>
          <TextField
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ minHeight: 400, maxHeight: 400 }}>
          {!loading && filteredAndSortedSpaces.length === 0 && !searchQuery ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No spaces yet. Create one below.
              </Typography>
            </Box>
          ) : filteredAndSortedSpaces.length === 0 && searchQuery ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                No spaces found matching "{searchQuery}"
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridAutoRows: 'max-content',
                gap: 1.5,
                height: 400,
                overflowY: 'auto',
                alignContent: 'start',
              }}
            >
              {filteredAndSortedSpaces.map((space, idx) => {
                const spaceColor = chartPalette[idx % chartPalette.length];
                const isSelected = selectedSpaceId === space.space_id;
                
                return (
                  <Card
                    key={space.space_id}
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      transition: 'all .2s ease',
                      position: 'relative',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: (theme) => theme.palette.mode === 'light'
                          ? '0 4px 12px rgba(16,24,40,0.12)'
                          : '0 4px 12px rgba(0,0,0,0.4)'
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelectedSpaceId(space.space_id)}
                      sx={{ alignItems: 'stretch', height: '100%' }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, pb: 1.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, mb: 1 }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            bgcolor: (theme) => theme.palette.mode === 'light' ? `${spaceColor}15` : `${spaceColor}25`,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mt: 0.25,
                          }}>
                            <FolderIcon sx={{ fontSize: 20, color: spaceColor }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography fontWeight={700} sx={{ fontSize: '0.9375rem', lineHeight: 1.3, mb: 0.5 }} noWrap>
                              {space.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: '0.8125rem', 
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {space.description || 'No description'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Created {formatRelativeTime(space.created_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                            {space.query_count || 0} {space.query_count === 1 ? 'insight' : 'insights'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Or create new category
        </Typography>
        <TextField
          label="New category name"
          fullWidth
          margin="normal"
          value={newSpaceName}
          onChange={(e) => setNewSpaceName(e.target.value)}
          placeholder="e.g., Executive Queries, Marketing Analytics"
        />
        {submitError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {submitError}
          </Alert>
        )}
    </AppDialog>
  );
}
